import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { escapeHtml, openPrintWindow } from '../utils/printUtils';
import {
  LogIn, PoundSterling, User, Users, CreditCard, Calendar,
  FileText, CheckCircle, Download, Printer, Image, X, Lock,
  Plus, Receipt, ChevronLeft, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CHURCH_LOGO_URL = '/images/church-logo.png?v=2';

const MobileHarvestRecord = () => {
  const [adminUser, setAdminUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Data
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [guests, setGuests] = useState([]);

  // Form
  const [contributorType, setContributorType] = useState('member');
  const [form, setForm] = useState({
    harvest_event_id: '', contributor_id: '', contributor_name: '',
    amount: '', payment_method: 'cash', date: new Date().toISOString().split('T')[0],
    notes: '', harvest_type: 'adult'
  });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [miniStatement, setMiniStatement] = useState(null);

  // Guest form
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestForm, setGuestForm] = useState({ full_name: '', phone: '', email: '' });

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, loginForm);
      const user = res.data.user;
      const perms = user.permissions || [];
      if (user.role !== 'main_admin' && !perms.includes('finance') && !perms.includes('projects')) {
        setLoginError('You do not have access to the Harvest module. Finance or Church Projects permission required.');
        setLoginLoading(false);
        return;
      }
      setAdminUser(user);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoginLoading(false);
  };

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [evRes, memRes, gstRes] = await Promise.all([
        axios.get(`${API}/harvest-events`),
        axios.get(`${API}/members`),
        axios.get(`${API}/guest-contributors`)
      ]);
      setEvents(evRes.data);
      setMembers(memRes.data);
      setGuests(gstRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (adminUser) loadData(); }, [adminUser, loadData]);

  // Select member/guest
  const handleContributorSelect = (id) => {
    if (contributorType === 'member') {
      const m = members.find(m => m.id === id);
      if (m) setForm(p => ({ ...p, contributor_id: m.id, contributor_name: m.full_name }));
    } else {
      const g = guests.find(g => g.id === id);
      if (g) setForm(p => ({ ...p, contributor_id: g.contributor_id || g.id, contributor_name: g.full_name }));
    }
  };

  // Add guest
  const handleGuestSubmit = async () => {
    if (!guestForm.full_name.trim()) { alert('Full name required'); return; }
    if (!guestForm.phone.trim()) { alert('Phone required'); return; }
    if (!guestForm.email.trim()) { alert('Email required'); return; }
    try {
      const res = await axios.post(`${API}/guest-contributors`, guestForm);
      setGuests(prev => [...prev, res.data]);
      setForm(p => ({ ...p, contributor_id: res.data.contributor_id || res.data.id, contributor_name: res.data.full_name }));
      setContributorType('guest');
      setShowGuestForm(false);
      setGuestForm({ full_name: '', phone: '', email: '' });
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add guest'); }
  };

  // Submit contribution
  const handleSubmit = async () => {
    if (!form.harvest_event_id) { alert('Please select a project'); return; }
    if (!form.contributor_id) { alert('Please select a contributor'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { alert('Please enter a valid amount'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/harvest-contributions`, {
        ...form,
        amount: parseFloat(form.amount),
        contributor_type: contributorType,
        received_by_id: adminUser?.id,
        received_by_name: adminUser?.full_name || adminUser?.username
      });
      // Load receipt
      const receiptRes = await axios.get(`${API}/harvest-contributions/receipt/${res.data.contribution.receipt_number}`);
      setReceipt(receiptRes.data.receipt);
      setMiniStatement(receiptRes.data.mini_statement);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to record contribution'); }
    setSubmitting(false);
  };

  // Reset form
  const resetForm = () => {
    setReceipt(null);
    setMiniStatement(null);
    setForm({
      harvest_event_id: form.harvest_event_id, contributor_id: '', contributor_name: '',
      amount: '', payment_method: 'cash', date: new Date().toISOString().split('T')[0],
      notes: '', harvest_type: 'adult'
    });
  };

  // Print receipt
  const handlePrint = () => {
    if (!receipt) return;
    const church = {};
    const prevTotal = miniStatement?.previous_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';
    const curContrib = miniStatement?.current_contribution?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';
    const totalDate = miniStatement?.total_to_date?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';

    const styles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Georgia', serif; background: #fff; }
      .receipt { max-width: 420px; margin: 0 auto; padding: 30px; position: relative; }
      .watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.08; width: 200px; height: 200px; pointer-events: none; z-index: 0; }
      .watermark img { width: 200px; height: 200px; object-fit: contain; }
      .content { position: relative; z-index: 1; }
      .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 15px; margin-bottom: 20px; }
      .church-logo { width: 80px; height: 80px; margin: 0 auto 10px; border-radius: 50%; overflow: hidden; }
      .church-logo img { width: 80px; height: 80px; object-fit: contain; }
      .church-name { font-size: 16px; font-weight: bold; color: #1e3a5f; text-transform: uppercase; }
      .receipt-badge { display: inline-block; margin-top: 12px; padding: 4px 16px; background: #eff6ff; border-radius: 20px; font-size: 11px; font-weight: bold; color: #1e3a5f; }
      .detail-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #e2e8f0; font-size: 13px; }
      .detail-label { color: #64748b; }
      .detail-value { font-weight: 600; color: #1e293b; }
      .amount-box { background: linear-gradient(135deg, #1e3a5f, #2563eb); color: white; padding: 16px; border-radius: 10px; margin: 16px 0; text-align: center; }
      .amount-value { font-size: 28px; font-weight: bold; }
      .footer { text-align: center; margin-top: 16px; font-style: italic; color: #1e3a5f; font-size: 13px; }
      @media print { .receipt { padding: 15px; } }
    `;
    const bodyHtml = `
      <div class="receipt">
        <div class="watermark"><img src="${CHURCH_LOGO_URL}" alt="" /></div>
        <div class="content">
          <div class="header">
            <div class="church-logo"><img src="${CHURCH_LOGO_URL}" /></div>
            <div class="church-name">Celestial Church of Christ — Valley of Berachah</div>
            <div class="receipt-badge">Harvest Contribution Receipt</div>
          </div>
          <div class="detail-row"><span class="detail-label">Receipt No</span><span class="detail-value" style="font-family:monospace">${escapeHtml(receipt.receipt_number)}</span></div>
          <div class="detail-row"><span class="detail-label">Date</span><span class="detail-value">${escapeHtml(receipt.date)}</span></div>
          <div class="detail-row"><span class="detail-label">Contributor ID</span><span class="detail-value" style="font-family:monospace">${escapeHtml(receipt.contributor_id_display || receipt.contributor_id)}</span></div>
          <div class="detail-row"><span class="detail-label">Contributor</span><span class="detail-value">${escapeHtml(receipt.contributor_name)}</span></div>
          <div class="detail-row"><span class="detail-label">Project</span><span class="detail-value">${escapeHtml(receipt.harvest_type)} Harvest</span></div>
          <div class="detail-row"><span class="detail-label">Payment</span><span class="detail-value">${escapeHtml((receipt.payment_method || '').replace('_', ' '))}</span></div>
          <div class="amount-box"><div style="font-size:11px;opacity:0.85">Amount</div><div class="amount-value">£${receipt.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div></div>
          ${miniStatement ? `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:14px;margin-bottom:16px;">
            <div style="font-size:11px;font-weight:bold;color:#92400e;margin-bottom:8px;">Statement</div>
            <div class="detail-row" style="border:none;font-size:12px;color:#92400e"><span>Previous</span><span>£${prevTotal}</span></div>
            <div class="detail-row" style="border:none;font-size:12px;color:#92400e"><span>This</span><span>£${curContrib}</span></div>
            <div class="detail-row" style="border-top:2px solid #d97706;font-size:13px;font-weight:bold;color:#78350f"><span>Total</span><span>£${totalDate}</span></div>
          </div>` : ''}
          <div class="footer">Thank you for your generous contribution!</div>
        </div>
      </div>
    `;
    openPrintWindow(`Receipt - ${receipt.receipt_number}`, styles, bodyHtml);
  };

  // ============ LOGIN SCREEN ============
  if (!adminUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm border-2 border-amber-300"
        >
          <div className="text-center mb-6">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 p-1 shadow-lg shadow-amber-500/30">
              <img src={CHURCH_LOGO_URL} alt="Church" className="w-full h-full rounded-full" />
            </div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-violet-800 to-purple-600 bg-clip-text text-transparent">Harvest Recorder</h1>
            <p className="text-sm text-slate-500 mt-1">Admin login required</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
              <input
                type="text" required autoFocus
                value={loginForm.username}
                onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                placeholder="Enter username"
                data-testid="harvest-login-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password" required
                value={loginForm.password}
                onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                placeholder="Enter password"
                data-testid="harvest-login-password"
              />
            </div>

            {loginError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {loginError}
              </div>
            )}

            <motion.button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 font-bold shadow-lg shadow-amber-500/30 hover:from-amber-400 hover:to-yellow-300 disabled:opacity-50 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="harvest-login-submit"
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </motion.button>
          </form>

          <div className="flex items-center gap-2 justify-center mt-4 text-xs text-slate-400">
            <Lock className="w-3 h-3" />
            Finance or Church Projects permission required
          </div>
        </motion.div>
      </div>
    );
  }

  // ============ RECEIPT VIEW ============
  if (receipt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-amber-50 p-4">
        <div className="max-w-md mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-5 text-center">
              <img src={CHURCH_LOGO_URL} alt="Church" className="w-14 h-14 mx-auto rounded-full mb-2 border-2 border-white/30" />
              <h2 className="text-lg font-bold text-white">Contribution Recorded!</h2>
              <p className="text-emerald-100 text-sm">{receipt.receipt_number}</p>
            </div>

            <div className="p-5 space-y-3">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span className="font-medium">{receipt.date}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">ID</span><span className="font-mono font-bold text-blue-700">{receipt.contributor_id_display || receipt.contributor_id}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Contributor</span><span className="font-medium">{receipt.contributor_name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Payment</span><span className="capitalize">{(receipt.payment_method || '').replace('_', ' ')}</span></div>

              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-4 text-center text-white mt-3">
                <div className="text-xs opacity-80">Amount</div>
                <div className="text-3xl font-bold">£{receipt.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
              </div>

              {miniStatement && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                  <div className="font-bold text-amber-800 text-xs mb-2">STATEMENT</div>
                  <div className="flex justify-between text-amber-700"><span>Previous</span><span>£{miniStatement.previous_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-amber-700"><span>This</span><span>£{miniStatement.current_contribution?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between font-bold text-amber-900 border-t border-amber-300 mt-2 pt-2"><span>Total</span><span>£{miniStatement.total_to_date?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span></div>
                </div>
              )}
            </div>

            <div className="p-4 grid grid-cols-4 gap-2">
              <button onClick={() => window.open(`${API}/harvest-contributions/receipt/${receipt.receipt_number}/pdf`, '_blank')} className="flex flex-col items-center gap-1 p-3 rounded-xl bg-blue-50 text-blue-600 text-xs font-medium">
                <Download className="w-5 h-5" />PDF
              </button>
              <button onClick={() => window.open(`${API}/harvest-contributions/receipt/${receipt.receipt_number}/image`, '_blank')} className="flex flex-col items-center gap-1 p-3 rounded-xl bg-purple-50 text-purple-600 text-xs font-medium">
                <Image className="w-5 h-5" />Image
              </button>
              <button onClick={handlePrint} className="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-50 text-amber-600 text-xs font-medium">
                <Printer className="w-5 h-5" />Print
              </button>
              <button onClick={resetForm} className="flex flex-col items-center gap-1 p-3 rounded-xl bg-emerald-50 text-emerald-600 text-xs font-medium">
                <Plus className="w-5 h-5" />New
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ============ RECORD FORM ============
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-violet-50 to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-800 via-purple-700 to-violet-800 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b-2 border-amber-400/60">
        <div className="flex items-center gap-3">
          <img src={CHURCH_LOGO_URL} alt="Church" className="w-9 h-9 rounded-full border-2 border-amber-400/70 shadow-lg shadow-amber-500/20" />
          <div>
            <h1 className="text-sm font-bold">Harvest Recorder</h1>
            <p className="text-[10px] text-amber-200">{adminUser.full_name} ({adminUser.role})</p>
          </div>
        </div>
        <button onClick={() => setAdminUser(null)} className="text-xs text-amber-100 hover:text-white px-3 py-1 rounded-lg bg-violet-900/60 hover:bg-violet-900 transition-colors border border-amber-400/30">
          Sign Out
        </button>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Project */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Select Project *</label>
          <select
            value={form.harvest_event_id}
            onChange={e => setForm(p => ({ ...p, harvest_event_id: e.target.value }))}
            className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none bg-white"
            data-testid="mobile-event-select"
          >
            <option value="">Select event...</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </div>

        {/* Harvest Type */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Harvest Type</label>
          <select value={form.harvest_type} onChange={e => setForm(p => ({ ...p, harvest_type: e.target.value }))}
            className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none bg-white">
            <option value="adult">Adult Harvest</option>
            <option value="children">Children Harvest</option>
            <option value="special">Special Harvest</option>
          </select>
        </div>

        {/* Contributor Type */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Contributor Type</label>
          <div className="grid grid-cols-2 gap-2">
            {['member', 'guest'].map(type => (
              <button key={type} onClick={() => { setContributorType(type); setForm(p => ({ ...p, contributor_id: '', contributor_name: '' })); }}
                className={`py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 border-2 transition-all ${contributorType === type ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}`}>
                {type === 'member' ? <User className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                {type === 'member' ? 'Member' : 'Guest'}
              </button>
            ))}
          </div>
        </div>

        {/* Contributor Select */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="text-sm font-semibold text-slate-700">
              Select {contributorType === 'member' ? 'Member' : 'Guest'} *
            </label>
            {contributorType === 'guest' && (
              <button onClick={() => setShowGuestForm(true)} className="text-xs text-amber-600 font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Guest
              </button>
            )}
          </div>
          <select
            value={form.contributor_id}
            onChange={e => handleContributorSelect(e.target.value)}
            className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none bg-white"
            data-testid="mobile-contributor-select"
          >
            <option value="">Select...</option>
            {(contributorType === 'member' ? members : guests).map(c => (
              <option key={c.id} value={c.id}>
                {c.full_name} {c.member_number ? `(${c.member_number})` : c.contributor_id ? `(${c.contributor_id})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Amount + Payment */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Amount (£) *</label>
            <input type="number" min="0.01" step="0.01" placeholder="0.00"
              value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
              data-testid="mobile-amount"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Payment Method</label>
            <select value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none bg-white">
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card</option>
              <option value="online">Online</option>
            </select>
          </div>
        </div>

        {/* Date + Notes */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
            <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Notes</label>
            <input type="text" placeholder="Optional" value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none" />
          </div>
        </div>

        <div className="text-xs text-slate-500 flex items-center gap-1">
          <User className="w-3 h-3" />
          Received by: {adminUser.full_name} ({adminUser.role === 'main_admin' ? 'Main Admin' : adminUser.role})
        </div>

        <motion.button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 font-bold text-base shadow-xl shadow-amber-500/40 hover:from-amber-400 hover:to-yellow-300 disabled:opacity-50 transition-all"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          data-testid="mobile-submit-btn"
        >
          {submitting ? 'Processing...' : 'Record Contribution & Generate Receipt'}
        </motion.button>
      </div>

      {/* Add Guest Modal */}
      <AnimatePresence>
        {showGuestForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowGuestForm(false)}>
            <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full sm:max-w-sm"
              onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-800">Add Guest Contributor</h3>
                <button onClick={() => setShowGuestForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <div className="space-y-3">
                <input type="text" placeholder="Full Name *" value={guestForm.full_name}
                  onChange={e => setGuestForm(p => ({ ...p, full_name: e.target.value }))}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none" />
                <input type="tel" placeholder="Phone Number *" value={guestForm.phone}
                  onChange={e => setGuestForm(p => ({ ...p, phone: e.target.value }))}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none" />
                <input type="email" placeholder="Email Address *" value={guestForm.email}
                  onChange={e => setGuestForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none" />
                <button onClick={handleGuestSubmit}
                  className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold">
                  Add Guest
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileHarvestRecord;
